import { Loader2, Upload } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import { useUploadAuditFiles } from "@/hooks/use-audit-files";
import { ACCEPTED_AUDIT_FILE_TYPES, MAX_AUDIT_FILE_SIZE_BYTES } from "@/lib/audit-file-limits";

interface ItemUploadButtonProps {
  auditId: string;
  itemId: string;
}

// Uploads land in the audit's shared attachment pool (same as AuditFilesButton),
// tagged with sourceItemId so the resulting link can be shown under this item.
export function ItemUploadButton({ auditId, itemId }: ItemUploadButtonProps) {
  const upload = useUploadAuditFiles(auditId, itemId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (selected.length === 0) return;

    const oversized = selected.filter((f) => f.size > MAX_AUDIT_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      const limit = MAX_AUDIT_FILE_SIZE_BYTES / (1024 * 1024);
      toast.error(
        oversized.length === 1
          ? `"${oversized[0]?.name}" exceeds the ${limit}MB limit`
          : `${oversized.length} files exceed the ${limit}MB limit`,
      );
      return;
    }

    upload.mutate(selected);
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_AUDIT_FILE_TYPES}
        className="hidden"
        onChange={handleFilesSelected}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={upload.isPending}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
      >
        {upload.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Upload className="h-3 w-3" />
        )}
        {upload.isPending ? "Uploading…" : "Upload"}
      </button>
    </>
  );
}
